/**
 * formDraft — localStorage-backed draft of the checkout form.
 *
 * Keeps a visitor's in-progress details so navigating away and coming back does
 * not mean retyping everything. The draft has a deliberate lifetime: it is
 * cleared once checkout succeeds, and any draft older than FORM_DRAFT_TTL_MS is
 * discarded on read, so contact details left on a shared machine expire on
 * their own even when the visitor never submits.
 *
 * The phone number is intentionally NOT persisted — it is optional, the least
 * useful field to restore, and the most sensitive thing to leave lying around.
 */

/** Exported so tests seed and inspect storage by the same key the helpers use. */
export const FORM_DRAFT_KEY = 'sendvc_form_data';

/** Long enough to survive a distracted visitor, short enough for a shared PC. */
export const FORM_DRAFT_TTL_MS = 60 * 60 * 1000; // 1 hour

export interface FormDraft {
  email: string;
  name: string;
  description: string;
  couponCode: string;
  selectedUpsells: string[];
}

const isBlank = (draft: FormDraft): boolean =>
  !draft.email && !draft.name && !draft.description && !draft.couponCode && draft.selectedUpsells.length === 0;

/**
 * Read the draft, or null if absent / unreadable / expired / not in a browser.
 * `now` is injectable so the expiry boundary is testable.
 */
export function readFormDraft(now: number = Date.now()): FormDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(FORM_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FormDraft> & { timestamp?: unknown };
    if (!parsed || typeof parsed !== 'object') return null;

    // No timestamp means a draft written before expiry existed — treat it as
    // stale rather than immortal, which is the whole point of the change.
    if (typeof parsed.timestamp !== 'number' || now - parsed.timestamp > FORM_DRAFT_TTL_MS) {
      clearFormDraft();
      return null;
    }

    return {
      email: typeof parsed.email === 'string' ? parsed.email : '',
      name: typeof parsed.name === 'string' ? parsed.name : '',
      description: typeof parsed.description === 'string' ? parsed.description : '',
      couponCode: typeof parsed.couponCode === 'string' ? parsed.couponCode : '',
      selectedUpsells: Array.isArray(parsed.selectedUpsells) ? parsed.selectedUpsells : [],
    };
  } catch {
    return null;
  }
}

/**
 * Persist the draft with the timestamp `readFormDraft` expires it by. An
 * all-empty draft is removed instead of stored, so a plain page visit does not
 * leave a record behind and a cleared draft is not resurrected by an idle save.
 *
 * The clock runs from the last *edit*, not the last save: restoring a draft
 * makes the form write back what it just read, and refreshing the timestamp
 * there would let a stranger's details outlive the window simply because the
 * page kept being opened.
 */
export function writeFormDraft(draft: FormDraft): void {
  if (typeof window === 'undefined') return;
  if (isBlank(draft)) {
    clearFormDraft();
    return;
  }
  try {
    // Fields are listed explicitly rather than spread, so nothing the caller
    // happens to carry (a phone number, a file) can leak into storage.
    const payload = {
      email: draft.email,
      name: draft.name,
      description: draft.description,
      couponCode: draft.couponCode,
      selectedUpsells: draft.selectedUpsells,
    };
    const stored = readFormDraft();
    if (stored && JSON.stringify(stored) === JSON.stringify(payload)) return;
    window.localStorage.setItem(FORM_DRAFT_KEY, JSON.stringify({ ...payload, timestamp: Date.now() }));
  } catch {
    /* out of quota / storage disabled — non-fatal, we just skip persistence */
  }
}

/** Drop the draft (checkout succeeded, or the stored copy went stale). */
export function clearFormDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(FORM_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}
